import React from "react";
import PropTypes from "prop-types";
import Ripple from "./ripple";
import styled from "styled-components";
import { theme } from "../theme";
import { Box } from "./layout";
import { rgba } from "polished";

// Helper function to get size styles
const getSizeStyles = (size) => {
  switch (size) {
    case "sm":
      return {
        padding: "0.375rem 0.75rem",
        fontSize: "0.8125rem",
        lineHeight: "2.0625rem",
        iconSize: "1.25rem",
      };
    case "lg":
      return {
        padding: "0.75rem 1.5rem",
        fontSize: "1.25rem",
        lineHeight: "3.25rem",
        iconSize: "1.25rem",
      };
    default: // medium
      return {
        padding: "0.625rem 1rem",
        fontSize: "1rem",
        lineHeight: "2.5rem",
        iconSize: "1.25rem",
      };
  }
};

// Helper to get color styles from the theme
const getColorStyles = (color, variant) => {
  const colorObj = theme.colors[color] || theme.colors.primary;
  const colorValue = colorObj[variant] || colorObj.main;
  return { colorValue, lightColor: colorObj.light || theme.colors.dark.light };
};

// ListItemStyle component
const ListItemStyle = styled.li(
  ({ size, disabled, onClick, sx, hover, isActive, activeColor, colorVariant }) => {
    const { padding } = getSizeStyles(size);
    const backgroundColor = isActive
      ? theme.colors[activeColor][colorVariant] || theme.colors[activeColor]?.main
      : "transparent";
    const hoverColor = theme.colors[hover].main || theme.colors.default.dark;

    return {
      width: "100%",
      padding,
      display: "flex",
      alignItems: "center",
      gap: "0.625rem",
      borderRadius: theme.radius.list,
      overflow: "hidden",
      position: "relative",
      opacity: disabled ? 0.6 : 1,
      pointerEvents:disabled && "none" ,
      cursor: onClick ? "pointer" : "default",
      fontFamily: theme.typography.fontFamily,
      transition: "background 0.3s ease",
      background: backgroundColor,
      "&:hover": {
        // background: hover ? rgba(theme.colors[hover]?.light, 0.15) : rgba(theme.colors.default.main, 0.15),
        background: rgba(hoverColor, 0.15),
      },
      ...sx,
    };
  }
);

// ListItem component
export const ListItem = ({
  children,
  className,
  disabled,
  onClick,
  sx,
  size,
  color,
  Colorvariant,
  hover,
  separator,
  gap,
  isActive,
  activeColor,
}) => {
  const { lightColor } = getColorStyles(color, Colorvariant);
  return (
    <>
      <ListItemStyle
        disabled={disabled}
        onClick={onClick}
        className={className}
        sx={sx}
        size={size}
        hover={hover}
        isActive={isActive}
        activeColor={activeColor}
      >
        {React.Children.map(children, (child) =>
          React.cloneElement(child, { color, Colorvariant })
        )}
        {onClick && <Ripple color={lightColor} duration={1000} />}
      </ListItemStyle>
      {separator && (
        <hr
          style={{
            margin: gap ? 0 : "0.5rem 0",
            border: "none",
            borderTop: `1px solid ${theme.colors.default.main}`,
          }}
        />
      )}
    </>
  );
};

// ListTextStyle component
const ListTextStyle = styled.div(({ size, color, Colorvariant, sx }) => {
  const { fontSize } = getSizeStyles(size);
  const { colorValue, lightColor } = getColorStyles(color, Colorvariant);
  return {
    maxWidth: "100%",
    flexBasis: 0,
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    gap: "0rem",
    ...sx,
    "& .primaryText": {
      fontSize,
      color: colorValue,
      fontWeight: 500,
      textTransform: "capitalize",
    },
    "& .secondaryText": {
      fontSize: `calc((${fontSize} / 4) * 3)`,
      color: lightColor,
    },
  };
});

// ListText component
export const ListText = ({
  primary,
  secondary,
  size,
  color,
  Colorvariant,
  sx,
  }) => (
  <ListTextStyle size={size} color={color} Colorvariant={Colorvariant} sx={sx}>
    <Box className="primaryText">{primary}</Box>
    {secondary && <Box className="secondaryText">{secondary}</Box>}
  </ListTextStyle>
);

// ListIconStyle component
const ListIconStyle = styled.div(({ size, color, Colorvariant, sx }) => {
  const { iconSize } = getSizeStyles(size);
  const { colorValue } = getColorStyles(color, Colorvariant);
  return {
    width: iconSize,
    height: iconSize,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
    color: colorValue,
    ...sx,
  };
});

// ListIcon component
export const ListIcon = ({ children, size, color, Colorvariant, sx }) => (
  <ListIconStyle size={size} color={color} Colorvariant={Colorvariant} sx={sx}>
    {children}
  </ListIconStyle>
);

// ListStyle component for the List wrapper
const ListStyle = styled.ul(({ gap }) => ({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: `${gap / 16}rem` || "0",
}));

// List component
export const List = ({
  children,
  color,
  className,
  size,
  Colorvariant,
  hover,
  gap,
  separator,
  activeColor,
}) => (
  <ListStyle className={className} gap={gap}>
    {React.Children.map(children, (child) =>
      React.cloneElement(child, {
        size,
        color,
        Colorvariant,
        hover,
        separator,
        activeColor,
      })
    )}
  </ListStyle>
);

// PropTypes for List
List.propTypes = {
  children: PropTypes.node.isRequired, // Required: should contain ListItem components
  color: PropTypes.oneOf(Object.keys(theme.colors)), // Optional: color prop for ListItem components
  className: PropTypes.string, // Optional: additional class names
  size: PropTypes.oneOf(["sm", "md", "lg"]), // Optional: size of the ListItem
  Colorvariant: PropTypes.oneOf(["main", "light", "dark"]), // Optional: color variant
  hover: PropTypes.oneOf(Object.keys(theme.colors)), // Optional: hover effect color
  gap: PropTypes.string, // Optional: gap between ListItems
  separator: PropTypes.bool, // Optional: whether to show a separator after each ListItem
  activeColor: PropTypes.oneOf(Object.keys(theme.colors)), // Optional: active color for active ListItem
};

// Default props for List
List.defaultProps = {
  color: "primary",
  className: "",
  size: "md",
  Colorvariant: "main",
  hover: "secondary",
  gap: "",
  separator: false,
  activeColor: "", // Default active color
};
